import { use, useMemo } from "react";
import { CompanyAccountingYearContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { BuContext } from "@domain/context/BuContext";
import { PaymentsContext } from "@domain/context/accountingData/PaymentsProvider";
import { computeVatAggregatedSt, computeVatPeriodRows } from "./vatPeriods";
import { type MetricStatus } from "./useGuVMetrics";

export interface VatPosition {
    status: MetricStatus;
    /** cents; positive = Zahllast (owed to Finanzamt), negative = Erstattung */
    amount: number;
    /** 1-12: the month whose Kennzahl 83 is shown */
    period: number;
}

export const getVatDisplayPeriod = (selectedYear: number, now: Date): number =>
    selectedYear === now.getFullYear() ? now.getMonth() + 1 : 12;

export const useVatPosition = (): VatPosition => {
    const { isReady } = use(ReadinessContext);
    const { companyBuTimeframes } = use(BuContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { allRecords } = use(RecordsContext);
    const payments = use(PaymentsContext);

    const yearRecords = useMemo(
        () =>
            (allRecords.list || []).filter((record) => record.date.getFullYear() === yearConfig?.year && !record.draft),
        [allRecords, yearConfig?.year],
    );

    return useMemo(() => {
        if (!isReady || !yearConfig || !companyBuTimeframes) {
            return { status: "loading" as const, amount: 0, period: 12 };
        }
        const period = getVatDisplayPeriod(yearConfig.year, new Date());
        const rows = computeVatPeriodRows(yearConfig, companyBuTimeframes, yearRecords, payments);
        if (rows.length === 0) {
            return { status: "empty" as const, amount: 0, period };
        }
        const kz83 = computeVatAggregatedSt(rows).get("83");
        return { status: "ready" as const, amount: kz83?.[period] ?? 0, period };
    }, [isReady, yearConfig, companyBuTimeframes, yearRecords, payments]);
};
